import React, { useState } from 'react'
import styled from 'styled-components'
import ordersApi from 'api/requests/orders'
import usePanelPermissions from 'hooks/usePanelPermissions'
import { PERMISSIONS } from 'helpers/panelPermissions'
import colors from 'theme/colors'

const ANSWER_MAX_LENGTH = 1000

const FALLBACK_ERROR = 'Não foi possível responder agora. Tente novamente em instantes.'

/**
 * Aceitar ou recusar o pedido de estorno do comprador, no detalhe do pedido
 * (RefundRequestCard): é o único lugar onde se responde, e o sino leva até ele.
 *
 * Tudo acontece no lugar, sem modal: confirmar e escrever o motivo ficam
 * dentro do próprio card.
 *
 * - Aceitar estorna o pedido (volta ao comprador, sai do saldo do produtor) e
 *   pede uma confirmação, porque não dá para desfazer. Depois que o evento
 *   termina a API não deixa aceitar: o botão some e o motivo aparece no lugar.
 * - Recusar exige o motivo, que o comprador recebe como notificação e lê na
 *   carteira. Recusar vale a qualquer momento.
 *
 * `onAnswered` recebe o que mudou no pedido (`status`, `answer`) depois que a
 * API confirmou a resposta.
 */
export default function RefundRequestActions({ refundRequest, onAnswered }) {
    const { can } = usePanelPermissions()
    const [mode, setMode] = useState(null)
    const [answer, setAnswer] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const reason = answer.trim()

    if (!can(PERMISSIONS.ORDERS_MANAGE)) {
        return <Note>Só quem gerencia os pedidos pode responder a este pedido.</Note>
    }

    function choose(next) {
        setError(null)
        setMode(next)
    }

    async function respond(request, patch) {
        setLoading(true)
        setError(null)

        try {
            await request()
            onAnswered(patch)
        } catch (err) {
            const data = err?.response?.data
            setError(data?.errors?.answer?.[0] || data?.message || FALLBACK_ERROR)
            setLoading(false)
        }
    }

    function accept() {
        respond(() => ordersApi.approveRefundRequest(refundRequest.id), { status: 'approved' })
    }

    function deny() {
        if (!reason) return

        respond(() => ordersApi.denyRefundRequest(refundRequest.id, reason), { status: 'denied', answer: reason })
    }

    if (mode === 'accept') {
        return (
            <Box>
                <Note>
                    O valor será devolvido ao comprador, descontado do seu saldo, e os ingressos deste pedido serão
                    cancelados. Essa ação não pode ser desfeita.
                </Note>
                {error && <ErrorText role='alert'>{error}</ErrorText>}
                <Buttons>
                    <Button type='button' $variant='ghost' disabled={loading} onClick={() => choose(null)}>Voltar</Button>
                    <Button type='button' $variant='success' $solid disabled={loading} onClick={accept}>
                        {loading ? 'Estornando…' : 'Confirmar estorno'}
                    </Button>
                </Buttons>
            </Box>
        )
    }

    if (mode === 'deny') {
        return (
            <Box>
                <Label htmlFor={`refund-answer-${refundRequest.id}`}>Motivo da recusa (o comprador vai ler)</Label>
                <Answer
                    id={`refund-answer-${refundRequest.id}`}
                    rows={3}
                    maxLength={ANSWER_MAX_LENGTH}
                    value={answer}
                    disabled={loading}
                    autoFocus
                    onChange={event => setAnswer(event.target.value)}
                />
                {error && <ErrorText role='alert'>{error}</ErrorText>}
                <Buttons>
                    <Button type='button' $variant='ghost' disabled={loading} onClick={() => choose(null)}>Voltar</Button>
                    <Button type='button' $variant='danger' $solid disabled={loading || !reason} onClick={deny}>
                        {loading ? 'Enviando…' : 'Recusar estorno'}
                    </Button>
                </Buttons>
            </Box>
        )
    }

    return (
        <Box>
            {!refundRequest.can_approve && refundRequest.blocked_reason && (
                <Note>Não dá para aceitar: {refundRequest.blocked_reason} Você ainda pode recusar.</Note>
            )}
            <Buttons>
                {refundRequest.can_approve && (
                    <Button type='button' $variant='success' onClick={() => choose('accept')}>Aceitar estorno</Button>
                )}
                <Button type='button' $variant='danger' onClick={() => choose('deny')}>Recusar</Button>
            </Buttons>
        </Box>
    )
}

// `ink` é a cor do texto e do preenchimento suave; a confirmação (`$solid`) enche o botão.
const VARIANTS = {
    success: { ink: colors.successDark },
    danger: { ink: colors.danger },
    ghost: { ink: colors.night },
}

const Box = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
`

const Note = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.night};
`

const Label = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
`

const Answer = styled.textarea`
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-family: inherit;
    font-size: 13px;
    color: ${colors.nightDark};
    resize: vertical;
    outline: none;

    &:focus {
        border-color: ${colors.occasionalPurple};
    }
`

const ErrorText = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.danger};
`

const Buttons = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`

const Button = styled.button`
    padding: 8px 14px;
    border-radius: 50px;
    border: 1px solid transparent;
    background: ${({ $variant, $solid }) => ($solid ? VARIANTS[$variant].ink : `${VARIANTS[$variant].ink}14`)};
    color: ${({ $variant, $solid }) => ($solid ? colors.neutralLight : VARIANTS[$variant].ink)};
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: filter 0.15s ease;

    &:hover:not(:disabled) {
        filter: brightness(0.95);
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`
